import { exactInteger, exactPositive } from '@/lib/exact'
import type { Block, Cell, Column, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Access & Audit › Audit log (`ac-audit`), read from `GET /v1/admin/audit`
 * (go-wallet#235, handler `internal/server/admin_access.go`).
 *
 * # The screen every other screen's promise depends on
 *
 * Every section of this console asserts that mutations are audited. Law 21 says
 * a mutation states what will be audited before it runs; invariant 6 in the
 * backend makes a failed audit write fail the operation it was recording. None
 * of that is worth anything to an auditor who cannot read the log, and until
 * this screen that was the position: the table existed, the trigger existed,
 * and no human could see a row without a database prompt.
 *
 * # What this screen must not have, and why the API agrees
 *
 * No row menu, no multi-select, no edit, no delete, no admin override, no bulk
 * anything. Law 5, and the endpoint's own description is the reason the absence
 * is honest rather than decorative:
 *
 *   > THIS IS THE WHOLE AUDIT SURFACE. One GET, and there is deliberately no
 *   > companion POST, PATCH, DELETE or bulk action at any permission…
 *
 * A read-only screen over a writable API is a UI convention. This is the other
 * thing.
 *
 * # Non-human actors are actors
 *
 * `system.reconciler`, `system.provider` and `customer.self` render AS actors,
 * with their own tone, never as a blank or as the word "system". "Who did this"
 * sometimes answers "the reconciler did, at 18:40, and opened case 4118", and
 * an operator reading a blank there learns nothing and assumes a bug.
 *
 * # There is no total, and the footer must not imply one
 *
 * The endpoint deliberately returns no count:
 *
 *   > counting an append-only table that grows forever, on every page view, is
 *   > how a read screen becomes an outage.
 *
 * So the footer says how many rows THIS PAGE carries against the limit that was
 * asked for, and says nothing about how many exist. The corpus footer reads
 * "1,204,412 events" and that figure is a drawing; rendering anything like it
 * from a page of 200 rows would be a fabricated count on the one screen whose
 * entire job is to be the record.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_access.go json tags
// ---------------------------------------------------------------------------

/** One row of `audit_log`, as `auditEntryView` serialises it. */
export interface AuditEntryView {
  readonly id: number
  readonly actor: string
  readonly action: string
  readonly subject?: string
  readonly request_id?: string
  readonly at: string
  /**
   * Unstructured detail as written. Carries `break_glass_session_id` on every
   * action taken while an elevation was live, which is what makes post-hoc
   * review a query rather than an archaeology of timestamps.
   */
  readonly attrs?: Readonly<Record<string, unknown>>
}

export interface AuditResponse {
  readonly entries: readonly unknown[]
  readonly limit?: unknown
}

export const AUDIT_PATH = '/audit'

const SOURCE = 'postgres · audit_log (GET /v1/admin/audit)'
const AUTHORITY = 'operational · append-only'

/**
 * How many rows to ask for.
 *
 * The endpoint caps at 200 and this asks for the cap. A smaller page would be
 * kinder to render and would mean the screen silently shows a slice of a window
 * an operator chose — and since there is no total to compare against, a slice
 * is indistinguishable from the whole. Asking for the maximum makes "this page
 * is full" a fact the footer can state.
 */
const PAGE = 200

// ---------------------------------------------------------------------------
// Reading a row
// ---------------------------------------------------------------------------

/** A field as a string, or `''`. Never a substituted value. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

interface EntryRow {
  readonly id: number | null
  readonly actor: string
  readonly action: string
  readonly subject: string
  readonly at: string
  readonly breakGlass: string
}

/**
 * The break-glass session id an action was taken under, if any.
 *
 * Read from `attrs` because that is where the backend writes it, and rendered
 * because it is the entire mechanism of post-hoc review: an elevation confers
 * no permission, and what makes it reviewable is that every row written during
 * its window carries its id. A row that carried one and did not show it would
 * hide the only trace an emergency leaves.
 */
function breakGlassOf(attrs: unknown): string {
  if (typeof attrs !== 'object' || attrs === null || Array.isArray(attrs)) return ''
  const raw = (attrs as Record<string, unknown>)['break_glass_session_id']
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
  return ''
}

/** One wire entry, or `null` if it is not an entry at all. */
function readEntry(raw: unknown): EntryRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const e = raw as Partial<AuditEntryView>
  // `exactInteger` (#207). The audit log is the record an auditor reads back,
  // and a row id wrong in its last digits cites a different row — in the one
  // place where citing the wrong row is the whole failure.
  const id = exactInteger(e.id)
  return {
    id,
    actor: text(e.actor),
    action: text(e.action),
    subject: text(e.subject),
    at: text(e.at),
    breakGlass: breakGlassOf(e.attrs),
  }
}

/**
 * The actor cell, with its kind read off the prefix the backend writes.
 *
 * `operator:`, `customer:` and `system:` are the three shapes the endpoint
 * documents. The tone separates them because an operator scanning for "what did
 * a human do" needs the reconciler's rows to be legible AND distinguishable —
 * and because an action attributed to `customer.self` on a compliance screen
 * means something quite different from the same action by an operator.
 */
function actorCell(actor: string): Cell {
  if (actor === '') {
    // Invariant 6 says every audited action is attributable. A blank actor is
    // that constraint having failed, not a system event, and it says so.
    return ['— unattributed', 'r']
  }
  if (actor.startsWith('system:') || actor.startsWith('system.')) return [actor, 'v']
  if (actor.startsWith('customer:') || actor.startsWith('customer.')) return [actor, 'n']
  return [actor, 'a']
}

const COLS: readonly Column[] = [
  ['Event', 'm', '104px'],
  ['At (UTC)', 'm', '156px'],
  ['Actor', 'c', 'minmax(160px,1.2fr)'],
  ['Action', 'm', 'minmax(210px,1.5fr)'],
  ['Subject', 'm', 'minmax(150px,1fr)'],
  ['Break-glass', 'm', '128px'],
]

/** `2026-07-31T11:04:22Z` → `2026-07-31 11:04:22`. Absence stays absent. */
function stamp(at: string): string {
  if (at === '') return ''
  const iso = at.replace('T', ' ')
  const dot = iso.indexOf('.')
  const trimmed = dot === -1 ? iso : `${iso.slice(0, dot)}${iso.slice(iso.length - 1)}`
  return trimmed.replace(/Z$/, '').trim()
}

function row(e: EntryRow): readonly Cell[] {
  return [
    e.id === null ? '— no id' : String(e.id),
    // Never blank. A blank cell in a timestamp column reads as "just now" to
    // the same glance that reads a blank count as zero.
    stamp(e.at) === '' ? '— not returned' : stamp(e.at),
    actorCell(e.actor),
    e.action === '' ? '— not recorded' : e.action,
    // A row with no subject is ordinary — a login, a read — so this is a dash
    // and not an alarm. It is the ACTOR that must always be present.
    e.subject === '' ? '—' : e.subject,
    e.breakGlass === '' ? '—' : e.breakGlass,
  ]
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

function auditTable(rows: readonly EntryRow[], limit: number | null, unreadable: number): TableBlock {
  const full = limit !== null && rows.length >= limit
  const head = `${String(rows.length)} row(s) on this page`
  const capped = full
    ? `${head} · the page is full, so there are almost certainly more`
    : `${head} · fewer than the ${String(limit ?? PAGE)} asked for, so this is the whole window`
  return {
    t: 'table',
    title: 'audit_log',
    src: 'ledger',
    // No filter was applied by this read, so no facet bar is drawn. An empty
    // facet bar implies a filter exists and that the window shown is narrower
    // than it is.
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: unreadable === 0 ? capped : `${capped} · ${String(unreadable)} row(s) could not be read`,
    foot2:
      'No total, deliberately: the endpoint does not count an append-only table on every page view, and a figure here would be one this console made up. No row menu, no multi-select, no edit, no delete — the API has no route for any of them either, which is what makes the absence honest rather than decorative.',
  }
}

/**
 * The note that states the law, on the screen rather than only in the code.
 *
 * Transcribed from the corpus, because the design already says it well and
 * because an auditor reading this screen is exactly who it is addressed to.
 */
const READ_ONLY_NOTE: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 5',
  title: 'The absence of controls is the feature',
  body: 'This screen has no row menu, no multi-select and no admin override, and neither does the API behind it: one GET, no companion write route at any permission, a row-level trigger refusing UPDATE and DELETE, and a read-only type on the admin surface with no write method to reach. The same is true of ledger_postings, ledger_legs, recognition_decisions, authz_decisions and provider_events.',
  ref: 'append-only · a break-glass id on a row is what makes post-hoc review a query',
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Audit log',
    title: 'No rows came back for this read',
    body: 'The trail itself is never empty — every mutation this console makes lands in it. So an empty page means the window that was read contains nothing, or that this deployment has recorded nothing yet. It does not mean the log is empty, and it is not evidence that nothing has happened.',
    ref: 'audit_log · newest first',
  }
}

function unreadableRowsNote(count: number): Block {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Partial read',
    title: `${String(count)} row(s) arrived and could not be read`,
    body: 'They are counted rather than dropped silently. A trail rendered shorter than the record, with nothing saying so, is the one failure this screen cannot have — an audit page that quietly omits rows is worse than one that does not load.',
    ref: 'the shape of those rows is not one this console knows',
  }
}

export function composeAuditLog(res: AuditResponse, readAt: Date): ScreenData {
  const rows: EntryRow[] = []
  let unreadable = 0
  for (const raw of res.entries) {
    const parsed = readEntry(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  // `exactPositive` (#207), which is the same rule this already had — a limit
  // of zero is a page with nothing on it — with the exactness added.
  const limit = exactPositive(res.limit)

  const blocks: Block[] = [auditTable(rows, limit, unreadable)]
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyNote())
  blocks.push(READ_ONLY_NOTE)

  // The page is a window, never the whole table, and the badge says so rather
  // than claiming a complete read of something that grows forever.
  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: AUTHORITY,
    readAt: readAtStamp(readAt),
    missing: ['how many rows the table holds — the endpoint returns no total, by design'],
  }

  return { blocks, provenance, rowKeys: rows.map((e) => (e.id === null ? null : String(e.id))) }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Audit log',
      title: 'The trail could not be read',
      body: `${reason} Nothing below is known — and an unread audit log is not an empty one. Every other screen in this console asserts that its mutations are recorded here; that assertion cannot be checked while this read is failing.`,
      ref: decisionRef === null
        ? 'No row was read. This is not an empty trail.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isAuditResponse(payload: unknown): payload is AuditResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<AuditResponse>).entries)
}

/**
 * Reads the audit trail.
 *
 * Resolves for every answer, per the loader contract. A 403 is the expected
 * answer for most sessions — the trail is Admin's and Compliance's — and it
 * must arrive carrying the `decision_ref` that explains it (law 13).
 */
export const loadAuditLog: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(AUDIT_PATH, { query: { limit: PAGE } })
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The audit endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isAuditResponse(payload)) {
    return unreadableScreen('The audit endpoint returned a shape this console does not know.', null)
  }
  return composeAuditLog(payload, new Date())
}
